import React, { useState, useRef, useEffect, useCallback, useContext } from "react";
import { Modal, CircularProgress } from "@mui/material";
import { changeStockStatus, FilterProduct } from "../../../../../config/Services";
import { UserContext } from "App";
import { useSnackbar } from "../../../product/hooks/SnackbarProvider";
import "./InventoryStock.css";

const InventoryStockModal = ({ open, onClose }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [items, setItems] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const scrollRef = useRef(null);
  const productsScrollRef = useRef(null);
  const { saleMode } = useContext(UserContext);
  const [loadingStockIds, setLoadingStockIds] = useState(new Set());
  const showSnackbar = useSnackbar();

  const locId = localStorage.getItem("locId");
  const orgId = localStorage.getItem("orgId");
  const retailUserDetails = JSON.parse(localStorage.getItem("retailUserDetails"));
  const appConfigData = localStorage.getItem("appConfig");
  const appConfig = appConfigData ? JSON.parse(localStorage.getItem("appConfig")) : {};

  // Stock status filters
  const filters = [
    { key: "All", label: "All", count: items?.length },
    { key: "InStock", label: "In Stock", count: items?.filter((i) => i?.inStock)?.length },
    { key: "LowStock", label: "Low Stock", count: items?.filter((i) => !i?.inStock)?.length },
    { key: "OutOfStock", label: "Out Of Stock", count: 0 },
    { key: "RestockedToday", label: "Restocked Today", count: 0 },
  ];

  // Fetch products from API
  const fetchProducts = useCallback(
    async (page = 1, resetItems = false) => {
      setLoadingProducts(true);
      try {
        const payload = {
          page: page,
          pageSize: 20,
          query: searchQuery || null,
          storeLocations: [locId],
          storeIds: [orgId],
          productTypes: ["RAW", "PREP", "ADD_ON", "CONSUMABLE"],
          salesChannels: ["DINE_IN", "TAKE_AWAY", "DELIVERY"],
          flattenResponse: true,
        };

        const response = await FilterProduct(payload);
        const result = await response?.data?.data;

        if (response?.data?.status === "SUCCESS" && result?.data?.data) {
          const newProducts = result?.data?.data || [];
          const formattedProducts = newProducts?.map((product) => {
            const salesChannelData = product?.variant?.externalSalesChannels?.find(
              (channel) => channel?.salesChannelName === saleMode
            );

            const stockStatus =
              salesChannelData?.stockStatus || product?.variant?.stockStatus || "OUT_OF_STOCK";
            const isInStock = stockStatus === "IN_STOCK";

            return {
              id: `${product?.productId}-${product?.variant?.variantId}-${saleMode}`,
              smallId: product?.id,
              productId: product?.productId,
              variantId: product?.variant?.variantId,
              salesChannel: saleMode,
              name: `${product?.name} ${product?.variant?.name}`,
              category: product?.appCategories?.categoryLevel1[0] || "Items",
              inStock: isInStock,
              stockStatus: isInStock ? "In Stock" : "Out of Stock",
              salePrice: salesChannelData?.salePrice || 0,
            };
          });

          if (resetItems) {
            setItems(formattedProducts);
          } else {
            setItems((prev) => [...prev, ...formattedProducts]);
          }

          setCurrentPage(page);
          setHasMore(page < (result?.data?.totalPages || 1));
        }
      } catch (error) {
      } finally {
        setLoadingProducts(false);
      }
    },
    [searchQuery, locId, saleMode]
  );

  useEffect(() => {
    if (open) {
      setCurrentPage(1);
      setItems([]);
      fetchProducts(1, true);
    }
  }, [open, activeFilter, searchQuery, fetchProducts]);

  const handleScroll = useCallback(() => {
    if (!productsScrollRef.current || loadingProducts || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = productsScrollRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      fetchProducts(currentPage + 1, false);
    }
  }, [loadingProducts, hasMore, currentPage, fetchProducts]);

  useEffect(() => {
    const scrollContainer = productsScrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
      return () => scrollContainer.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  const handleToggleStock = async (id, item) => {
    if (!appConfig?.enabledFeatures?.ALLOW_MARKING_ITEMS_FROM_KDS) {
      showSnackbar("Marking out of stock feature is disabled in POS configurations", "error");
      return;
    }
    if (loadingStockIds.has(id)) return;

    setLoadingStockIds((prev) => new Set([...prev, id]));
    const originalInStock = item.inStock;

    setItems((prevItems) =>
      prevItems.map((i) => (i.id === id ? { ...i, inStock: !i.inStock } : i))
    );

    try {
      const payload = {
        productId: item?.smallId,
        orgId: orgId,
        locationId: locId,
        variantStockRequests: [
          {
            variantId: item?.variantId,
            salesChannel: ["DINE_IN", "TAKE_AWAY", "DELIVERY", "SWIGGY", "ZOMATO"],
            markAll: false,
          },
        ],
        stockStatus: originalInStock ? "OUT_OF_STOCK" : "IN_STOCK",
        markAll: false,
        updatedBy: retailUserDetails?.uidx,
        updatedByName: retailUserDetails?.firstName,
      };

      const response = await changeStockStatus(payload);
      const responseData = response?.data?.data;

      if (
        responseData?.statusCode === 10001 ||
        responseData?.message?.toLowerCase().includes("no products found")
      ) {
        showSnackbar(responseData?.message || "No products found", "error");
        setItems((prevItems) =>
          prevItems?.map((i) => (i?.id === id ? { ...i, inStock: originalInStock } : i))
        );
        return;
      }

      showSnackbar(`Item marked as ${originalInStock ? "Out of Stock" : "In Stock"}`, "success");
    } catch (error) {
      setItems((prevItems) =>
        prevItems?.map((i) => (i?.id === id ? { ...i, inStock: originalInStock } : i))
      );
      showSnackbar("Failed to update stock status. Please try again.", "error");
    } finally {
      setLoadingStockIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: "flex", justifyContent: "flex-end", alignItems: "stretch" }}
    >
      <div className="inventory-stock-drawer">
        {/* Header */}
        <div className="inventory-stock-header">
          <h2 className="inventory-stock-title">Inventory Stock Update</h2>
          <button
            className="inventory-stock-close-btn"
            onClick={onClose}
            data-testid="button-close-inventory-stock-drawer"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-x h-4 w-4"
              aria-hidden="true"
            >
              <path d="M18 6 6 18"></path>
              <path d="m6 6 12 12"></path>
            </svg>
          </button>
        </div>

        {/* Filters Section */}
        <div className="inventory-stock-filters-section">
          <div className="inventory-stock-filters-card">
            <div className="inventory-stock-filters-layout">
              <div className="inventory-stock-tabs-container">
                <div className="inventory-stock-tabs-list">
                  {filters?.map((filter) => (
                    <button
                      key={filter?.key}
                      className={`inventory-stock-tab-btn ${
                        activeFilter === filter?.key ? "active" : ""
                      }`}
                      onClick={() => setActiveFilter(filter?.key)}
                      data-testid={`button-inventory-stock-tab-${filter?.key.toLowerCase()}`}
                    >
                      <span>{filter?.label}</span>
                      {filter?.count !== undefined && (
                        <span className="inventory-stock-tab-count">{filter?.count}</span>
                      )}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-scale h-4 w-4"
                        aria-hidden="true"
                      >
                        <path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"></path>
                        <path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"></path>
                        <path d="M7 21h10"></path>
                        <path d="M12 3v18"></path>
                        <path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2"></path>
                      </svg>
                      {filter?.key === "RestockedToday" && (
                        <span style={{ color: "#10b981", marginLeft: "4px" }}>•</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="inventory-stock-search-wrapper">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-search h-4 w-4 text-slate-400"
                  aria-hidden="true"
                >
                  <path d="m21 21-4.34-4.34"></path>
                  <circle cx="11" cy="11" r="8"></circle>
                </svg>
                <input
                  placeholder="Search items"
                  className="inventory-stock-search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="input-inventory-stock-search"
                  type="text"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="inventory-stock-grid-container">
          <div className="inventory-stock-grid-viewport" ref={productsScrollRef}>
            <div className="inventory-stock-grid">
              {items?.map((item, index) => (
                <div
                  key={item?.id}
                  className="inventory-stock-card"
                  data-testid={`card-inventory-stock-${index}`}
                >
                  <div className="inventory-stock-card-content">
                    <p className="inventory-stock-card-title">{item?.name}</p>
                    <p className="inventory-stock-card-category">{item?.category}</p>
                  </div>
                  <div
                    className={`inventory-stock-card-footer ${
                      item?.inStock ? "in-stock" : "low-stock"
                    }`}
                  >
                    <span className="inventory-stock-status-text">
                      {loadingStockIds?.has(item?.id)
                        ? "Updating..."
                        : item?.inStock
                        ? "In Stock"
                        : "Low Stock"}
                    </span>
                    <div
                      className={`custom-switch ${item?.inStock ? "checked" : "unchecked"} ${
                        !item?.inStock ? "unchecked-in-red-bg" : ""
                      }`}
                      onClick={() => handleToggleStock(item?.id, item)}
                    >
                      <span className="custom-switch-thumb"></span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {loadingProducts && (
              <div style={{ display: "flex", justifyContent: "center", padding: "24px" }}>
                <CircularProgress size={30} />
              </div>
            )}

            {!loadingProducts && items.length === 0 && (
              <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                No items found
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default InventoryStockModal;
